import { cn } from 'cn'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DESC_SIZES,
  META_SIZES,
  TITLE_SIZES,
  useAccessibility,
} from '@/accessibility/use-accessibility'
import { LangToggle } from '@/player/lang-toggle'

interface CoachmarkCardProps {
  lessonLabel: string
  /** Index of the current step among ALL steps in the tour, `explore` beats
   *  included — the counter's denominator is the lesson's real length, not a
   *  subset of it. Explore steps still carry their true index; the card just
   *  does not render a number for them (see `explore` below). */
  stepIndex: number
  /** IDs of every step in the tour, in order — drives the progress bar segments. */
  stepIds: readonly string[]
  /** True when this is the first step in the full tour (used to disable the Back button). */
  isFirst: boolean
  /** True when this is the last step in the full tour (used for the Finish button). */
  isLast: boolean
  /** When true this is an orientation beat: hides the step counter and progress
   *  bar ON THIS STEP, and always shows the Next button regardless of spotlight
   *  type. It does NOT remove the step from `stepIds` — excluding orientation
   *  beats from the total is what made the counter under-report lesson length. */
  explore?: boolean
  /** When true the card renders with tighter padding/spacing and a lower height cap,
   *  so it stays clear of a tall centred modal (e.g. the date-time picker) it sits over. */
  compact?: boolean
  /** When true the card renders larger — a taller height cap and a bumped
   *  headline/body type scale — to emphasise a key explanation beat. */
  emphasis?: boolean
  /** Pixels of room the positioner actually left below the card, passed only when
   *  that is tighter than the class cap. Without it a card anchored under a
   *  low target would honour its 50svh cap and hang its footer — and the Next
   *  button — off the bottom of the screen. */
  maxHeight?: number
  headline: string
  body: string
  /** Optional caption rendered below the body in a lighter style. */
  note?: string
  /** True when the step has no live target — shown so a learner is never told to tap nothing. */
  narrative: boolean
  /** When true, suppresses the "not built yet" note even for narrative steps (intentional pass-through). */
  hideNarrativeNote?: boolean
  /** True when the step has a live target — learner must tap the highlighted element to advance. */
  requiresInteraction: boolean
  /** True when the required interaction (tap or typed input) has been completed. */
  interactionComplete: boolean
  /** When true, exit skips the confirmation dialog and labels the button "Exit tour". */
  isTour?: boolean
  onPrevious: () => void
  onNext: () => void
  onExit: () => void
}

/**
 * Renders the `**bold**` markdown subset used in step bodies.
 *
 * Keyed by index, which normally deserves suspicion — here it is the correct
 * identity. `split` derives the fragments positionally from one string: the
 * array is regenerated whole whenever `body` changes and is never reordered,
 * filtered, or spliced, so fragment N is always fragment N of the body being
 * rendered. Keying on the content was the bug: a body may legitimately repeat a
 * bold phrase — task-management `l1-07` names `**My Tasks**` twice, still live
 * in `task-management.zh.json` — which hands React duplicate keys and lets it
 * reconcile the wrong fragment when the card re-renders across steps whose
 * bodies share bold text.
 */
export function renderBodyText(text: string) {
  // Each fragment carries an id minted at split time rather than its array
  // index, so a body that repeats a bold phrase still hands React two distinct
  // keys — see the note above.
  const parts = text.split(/(\*\*.*?\*\*)/).map((text, i) => ({ id: `f${i}`, text }))
  return parts.map(({ id, text: part }) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={id}>{part.slice(2, -2)}</strong>
    ) : (
      part
    ),
  )
}

/**
 * A body laid out the way its author wrote it.
 *
 * Authors already write lists — `l4-03a` enumerates six return statuses one per
 * line, each prefixed `- ` — but a single `<p>` collapses every newline, so six
 * bullets arrived as one wall of text. Blank lines separate paragraphs and a
 * run of `- ` lines becomes a `<ul>`; anything without newlines renders exactly
 * as before, which is every other body in the content set.
 */
interface BodyBlock {
  /** Minted when the block is built, so the key is never an array index. */
  id: string
  kind: 'p' | 'ul'
  lines: string[]
  /** Paragraphs after the first carry a top margin. */
  spaced: boolean
}

/** Group the body's lines into paragraphs and bullet lists, in order. */
function bodyBlocks(text: string): BodyBlock[] {
  const blocks: BodyBlock[] = []
  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (line === '') continue
    const isBullet = line.startsWith('- ')
    const content = isBullet ? line.slice(2).trim() : line
    const last = blocks.at(-1)
    // Consecutive bullets share one list; consecutive prose lines do not share
    // a paragraph, because a newline between them was meant as a break.
    if (isBullet && last?.kind === 'ul') last.lines.push(content)
    else
      blocks.push({
        id: `b${blocks.length}`,
        kind: isBullet ? 'ul' : 'p',
        lines: [content],
        spaced: blocks.length > 0,
      })
  }
  return blocks
}

export function renderBody(text: string) {
  return bodyBlocks(text).map((block) =>
    block.kind === 'ul' ? (
      <ul key={block.id} className="mt-1 list-disc space-y-1 pl-5">
        {block.lines.map((line) => (
          <li key={line}>{renderBodyText(line)}</li>
        ))}
      </ul>
    ) : (
      <p key={block.id} className={block.spaced ? 'mt-2' : undefined}>
        {renderBodyText(block.lines[0] ?? '')}
      </p>
    ),
  )
}

function ExitDialog({
  open,
  fontScale,
  onKeepGoing,
  onExit,
}: Readonly<{
  open: boolean
  fontScale: number
  onKeepGoing: () => void
  onExit: () => void
}>) {
  const { t } = useTranslation('player')
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onKeepGoing()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onKeepGoing])

  return (
    <div className="p-5 flex flex-col gap-4">
      <div className="flex flex-col gap-1.5 text-center">
        <p className={cn('font-bold text-foreground', TITLE_SIZES[fontScale])}>
          {t('exitThisLesson')}
        </p>
        <p className={cn('text-muted-foreground', DESC_SIZES[fontScale])}>
          {t('progressNotSaved')}
        </p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onKeepGoing}
          className={cn(
            'flex min-h-(--spacing-touch) flex-1 items-center justify-center rounded-xl border border-border px-4 font-semibold text-foreground',
            META_SIZES[fontScale],
          )}
        >
          {t('keepGoing')}
        </button>
        <button
          type="button"
          onClick={onExit}
          className={cn(
            'flex min-h-(--spacing-touch) flex-1 items-center justify-center rounded-xl bg-destructive px-4 font-bold text-destructive-foreground',
            META_SIZES[fontScale],
          )}
        >
          {t('exitLesson')}
        </button>
      </div>
    </div>
  )
}
const CARD_SHELL =
  'pointer-events-auto w-full rounded-2xl border border-border bg-card shadow-2xl flex flex-col'

/**
 * How tall the card may grow.
 *
 * The cap keeps step copy from swallowing the screen it is explaining.
 * 50svh is the ceiling, not a guess: `coachmarkPosition` already reserves
 * `vpHeight / 2` for the card in every clamp, so anything below 50svh left
 * reserved space unused and pushed long copy at font scale >=2 into the
 * inner scroller for no reason. Where the halo-clearance floor overrides
 * that reservation on short viewports, `coachmarkPosition` returns a
 * narrower `maxHeight` and the inline style wins over this class.
 *
 * The exit confirmation is not step copy: in landscape, or at font scale >=2,
 * it needs 168-209px against a ~192px cap, and `overflow-hidden` clipped BOTH
 * buttons off the bottom — no "Exit lesson" and no "Keep going", so a learner
 * who opened it could neither leave the lesson nor dismiss the dialog.
 */
/**
 * Below this measured height a card cannot afford its full chrome.
 *
 * 260 = 164px of header and footer plus ~96px for a headline and two lines of
 * body. At 189px — the room left beside the description field on a 394x540
 * visual viewport, i.e. a normal phone with the keyboard open — the full card
 * renders its label, counter and progress bar and leaves 25px for the sentence
 * the learner has to read.
 */
const TIGHT_CARD = 260

function cardFit(exitDialogOpen: boolean, compact: boolean, bounded: boolean): string {
  if (exitDialogOpen) return 'max-h-[80svh] overflow-y-auto'
  // `compact` stays tight on purpose — it exists to keep the card clear of a
  // centred date-time picker, which a taller card would cover.
  const cap = compact ? 'max-h-[22svh]' : 'max-h-[50svh]'
  // When the overlay hands down an inline `maxHeight` it has measured the real
  // room left beside the target, which can be tighter than either class cap.
  // `overflow-hidden` then silently slices the copy — the learner sees half a
  // sentence and no hint that there is more. Scroll instead.
  if (bounded) return `${cap} overflow-y-auto`
  return `${cap} overflow-hidden`
}

interface CoachmarkMainProps extends Omit<CoachmarkCardProps, 'onExit'> {
  fontScale: number
  boldText: boolean
  onExitRequest: () => void
  onExitDirect?: () => void
}

/**
 * The step's body copy.
 *
 * A body with no newlines stays a single `<p>` — that is every body but two —
 * so the common case keeps the bold-text class on the element the text is in.
 * A multi-line one needs a `<div>` wrapper, because it may contain a `<ul>`.
 */
function CoachmarkBody({
  body,
  bodySize,
  boldText,
}: Readonly<{ body: string; bodySize: string | undefined; boldText: boolean }>) {
  const className = cn(
    'leading-relaxed',
    bodySize,
    boldText ? 'font-bold text-foreground' : 'text-foreground',
  )
  if (body.includes('\n')) return <div className={className}>{renderBody(body)}</div>
  return <p className={className}>{renderBodyText(body)}</p>
}

function CoachmarkMain({
  lessonLabel,
  stepIndex,
  stepIds,
  isFirst,
  isLast,
  explore = false,
  headline,
  body,
  note,
  compact = false,
  emphasis = false,
  isTour = false,
  requiresInteraction,
  interactionComplete,
  fontScale,
  boldText,
  onPrevious,
  onNext,
  onExitRequest,
  onExitDirect,
}: Readonly<CoachmarkMainProps>) {
  const { t } = useTranslation('player')
  const stepCount = stepIds.length
  // Emphasis bumps the type one scale step (clamped to the largest defined size)
  // so a key explanation reads larger without touching the learner's font setting.
  const emphasisScale = Math.min(fontScale + 1, TITLE_SIZES.length - 1) as typeof fontScale
  const titleSize = emphasis ? TITLE_SIZES[emphasisScale] : TITLE_SIZES[fontScale]
  // The body carries the instruction the learner actually acts on, so it reads
  // one scale step above their base setting; emphasis takes it one further.
  const bodyScale = Math.min(
    fontScale + (emphasis ? 2 : 1),
    DESC_SIZES.length - 1,
  ) as typeof fontScale
  const bodySize = DESC_SIZES[bodyScale]
  const scrollRef = useRef<HTMLDivElement>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: stepIndex and headline are intentional triggers, not used in the effect body
  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 })
  }, [stepIndex, headline])
  const canAdvance = !requiresInteraction || interactionComplete || isLast || explore
  const nextLabel = isLast ? t('finishLesson') : t('nextStep')
  const nextIcon = isLast ? null : <ChevronRight className="size-4" />

  return (
    <>
      {/* Fixed header — lesson label, step counter, lang toggle, progress bar */}
      <div className={cn('px-5 shrink-0 touch-none', compact ? 'pt-2' : 'pt-5')}>
        <div className={cn('flex items-start justify-between gap-2', compact ? 'mb-2' : 'mb-3')}>
          {/* `compact` cards sit in the sliver above a modal dialog, so the
              lesson label, step counter and progress bar below are dropped to
              buy the body its lines back. The language toggle stays — losing it
              mid-lesson would strand a reader in the wrong language. The empty
              div keeps the toggle right-aligned. */}
          <div className="min-w-0 flex-1">
            {!compact && (
              <>
                <p
                  className={cn(
                    'font-semibold text-foreground leading-snug',
                    META_SIZES[fontScale],
                  )}
                >
                  {lessonLabel}
                </p>
                {!explore && (
                  <p className={cn('text-muted-foreground', META_SIZES[fontScale])}>
                    {t('stepOf', { current: stepIndex + 1, total: stepCount })}
                  </p>
                )}
              </>
            )}
          </div>
          <LangToggle />
        </div>

        {!explore && !compact && (
          <div className="mb-3 flex gap-1" aria-hidden="true">
            {stepIds.map((id, i) => (
              <span
                key={id}
                className={cn(
                  'h-1 flex-1 rounded-full',
                  i <= stepIndex ? 'bg-primary' : 'bg-muted',
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* Scrollable content — headline, body, note */}
      <div
        ref={scrollRef}
        data-coachmark-scroll
        /*
         * A step whose target sits inside a sheet or dialog is described by a
         * card that lives OUTSIDE that dialog, in the tour overlay. Radix wraps
         * modal content in `react-remove-scroll`, which listens for `wheel` and
         * `touchmove` on `document` and cancels every one whose target is
         * outside the dialog — this scroller included. The copy was therefore
         * clipped with no way to reach the rest of it, first seen on `l4-12` at
         * 360x800 where the card is capped by the sheet above it.
         *
         * React delegates both events at the root container, below `document`,
         * so stopping propagation here means the sidecar never sees them and
         * the card scrolls natively. Same remedy as the overlay's own
         * `onPointerDown` guard against Radix's dismiss listener.
         */
        onTouchMove={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        className="overflow-y-auto px-5 flex-1 min-h-0 pointer-events-auto touch-pan-y"
      >
        <h2 className={cn('mb-1 font-bold text-foreground leading-snug tracking-tight', titleSize)}>
          {headline}
        </h2>
        <CoachmarkBody body={body} bodySize={bodySize} boldText={boldText} />

        {note ? (
          <p
            className={cn(
              'mt-2 italic',
              META_SIZES[fontScale],
              boldText ? 'font-bold text-foreground' : 'text-muted-foreground',
            )}
          >
            {note}
          </p>
        ) : null}
      </div>

      {/* Fixed footer — navigation buttons */}
      <div className={cn('px-5 shrink-0 touch-none', compact ? 'pb-2 pt-2' : 'pb-5 pt-4')}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={isTour ? onExitDirect : onExitRequest}
            className={cn(
              'flex min-h-(--spacing-touch) items-center gap-1 rounded-xl border border-border px-3 font-semibold text-foreground whitespace-nowrap',
              META_SIZES[fontScale],
            )}
          >
            {isTour ? t('exitTour') : t('exitLesson')}
          </button>
          <button
            type="button"
            onClick={onPrevious}
            disabled={isFirst}
            className={cn(
              'flex min-h-(--spacing-touch) items-center gap-1 rounded-xl border border-border px-3 font-semibold text-foreground disabled:opacity-40 whitespace-nowrap',
              META_SIZES[fontScale],
            )}
          >
            <ChevronLeft className="size-4" />
            {t('back')}
          </button>

          {canAdvance ? (
            <button
              type="button"
              onClick={onNext}
              className={cn(
                'flex min-h-(--spacing-touch) flex-1 min-w-fit items-center justify-center gap-1 rounded-xl bg-primary px-3 font-bold text-primary-foreground shadow-md whitespace-nowrap',
                META_SIZES[fontScale],
              )}
            >
              {nextLabel}
              {nextIcon}
            </button>
          ) : null}
        </div>
      </div>
    </>
  )
}

export function CoachmarkCard({
  lessonLabel,
  stepIndex,
  stepIds,
  isFirst,
  isLast,
  explore = false,
  headline,
  body,
  note,
  narrative,
  hideNarrativeNote = false,
  compact = false,
  emphasis = false,
  isTour = false,
  maxHeight,
  requiresInteraction,
  interactionComplete,
  onPrevious,
  onNext,
  onExit,
}: Readonly<CoachmarkCardProps>) {
  const { t } = useTranslation('player')
  const { fontScale, boldText } = useAccessibility()
  const stepCount = stepIds.length
  const [exitDialogOpen, setExitDialogOpen] = useState(false)

  // A card the overlay has bounded tightly spends most of its height on chrome:
  // the lesson label, step counter and progress bar are ~90px of a 189px card,
  // which is how a learner ended up seeing "Description" and none of the
  // sentence telling them what to type. Below the threshold, shed that chrome —
  // the same thing `compact` already does — so the copy gets the room instead.
  // The step counter is the only real loss, and it is still in the aria-label.
  const tight = maxHeight !== undefined && maxHeight < TIGHT_CARD
  const lean = compact || tight

  return (
    <section
      aria-label={
        explore
          ? `${lessonLabel}, quick tour`
          : `${lessonLabel}, ${t('stepOf', { current: stepIndex + 1, total: stepCount })}`
      }
      className={cn(CARD_SHELL, cardFit(exitDialogOpen, compact, maxHeight !== undefined))}
      // Inline beats the class cap, and is only ever the narrower of the two.
      // The exit dialog keeps its own 80svh — it must stay reachable.
      style={maxHeight !== undefined && !exitDialogOpen ? { maxHeight } : undefined}
    >
      {!isTour && exitDialogOpen ? (
        // Inline exit confirmation — rendered inside the card which lives inside the
        // z-[100] TourOverlay, so it is always above blockInteraction divs and scrim
        // panels. A portal dialog at z-[90] would be blocked by the z-[100] overlay.
        <ExitDialog
          open={exitDialogOpen}
          fontScale={fontScale}
          onKeepGoing={() => setExitDialogOpen(false)}
          onExit={onExit}
        />
      ) : (
        <CoachmarkMain
          lessonLabel={lessonLabel}
          stepIndex={stepIndex}
          stepIds={stepIds}
          isFirst={isFirst}
          isLast={isLast}
          explore={explore}
          headline={headline}
          body={body}
          note={note}
          narrative={narrative}
          hideNarrativeNote={hideNarrativeNote}
          compact={lean}
          emphasis={emphasis}
          isTour={isTour}
          requiresInteraction={requiresInteraction}
          interactionComplete={interactionComplete}
          fontScale={fontScale}
          boldText={boldText}
          onPrevious={onPrevious}
          onNext={onNext}
          onExitRequest={() => setExitDialogOpen(true)}
          onExitDirect={onExit}
        />
      )}
    </section>
  )
}
